import type { MarkerFeature } from "../../../../../utils/models/MarkerFeature";
import type { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import type { UndoCommand, CommandContext } from "../types";

/**
 * Command for merging all route-style routes into one and pulling every
 * remaining route into the same day. Stores snapshots for full restoration.
 */
export class MergeAllRoutesCommand implements UndoCommand {
  readonly type = 'MERGE_ALL_ROUTES';
  readonly routeKey: string;

  private keepRouteKey: string;
  private removedRouteSnapshots: { key: string; snapshot: any }[];
  private keepRouteMarkersBefore: MarkerFeature[];
  private keepRouteLineStringsBefore: LineStringFeature[];
  private keepRouteMarkersAfter: MarkerFeature[];
  private keepRouteLineStringsAfter: LineStringFeature[];
  private routePositionsBefore: { key: string; position: number }[];
  private routePositionsAfter: { key: string; position: number }[];
  private routeDaysBefore: { key: string; day: number | undefined }[];
  private targetDay: number;
  private activeRouteKeyBefore: string;

  constructor(data: {
    keepRouteKey: string;
    removedRouteSnapshots: { key: string; snapshot: any }[];
    keepRouteMarkersBefore: MarkerFeature[];
    keepRouteLineStringsBefore: LineStringFeature[];
    keepRouteMarkersAfter: MarkerFeature[];
    keepRouteLineStringsAfter: LineStringFeature[];
    routePositionsBefore: { key: string; position: number }[];
    routePositionsAfter: { key: string; position: number }[];
    routeDaysBefore: { key: string; day: number | undefined }[];
    targetDay: number;
    activeRouteKeyBefore: string;
  }) {
    this.keepRouteKey = data.keepRouteKey;
    this.routeKey = data.keepRouteKey;
    this.removedRouteSnapshots = JSON.parse(JSON.stringify(data.removedRouteSnapshots));
    this.keepRouteMarkersBefore = JSON.parse(JSON.stringify(data.keepRouteMarkersBefore));
    this.keepRouteLineStringsBefore = JSON.parse(JSON.stringify(data.keepRouteLineStringsBefore));
    this.keepRouteMarkersAfter = JSON.parse(JSON.stringify(data.keepRouteMarkersAfter));
    this.keepRouteLineStringsAfter = JSON.parse(JSON.stringify(data.keepRouteLineStringsAfter));
    this.routePositionsBefore = data.routePositionsBefore;
    this.routePositionsAfter = data.routePositionsAfter;
    this.routeDaysBefore = data.routeDaysBefore;
    this.targetDay = data.targetDay;
    this.activeRouteKeyBefore = data.activeRouteKeyBefore;
  }

  undo(ctx: CommandContext): void {
    const keepRoute = ctx.state.routes[this.keepRouteKey];
    if (!keepRoute) return;

    keepRoute.markers = JSON.parse(JSON.stringify(this.keepRouteMarkersBefore));
    keepRoute.lineStrings = JSON.parse(JSON.stringify(this.keepRouteLineStringsBefore));
    keepRoute.updated_at = new Date();

    for (const { key, snapshot } of this.removedRouteSnapshots) {
      ctx.state.routes[key] = JSON.parse(JSON.stringify(snapshot));
    }

    for (const { key, position } of this.routePositionsBefore) {
      if (ctx.state.routes[key]) {
        ctx.state.routes[key].position = position;
      }
    }

    for (const { key, day } of this.routeDaysBefore) {
      if (ctx.state.routes[key]) {
        ctx.state.routes[key].day = day;
      }
    }

    ctx.state.activeRouteKey = this.activeRouteKeyBefore;
  }

  redo(ctx: CommandContext): void {
    const keepRoute = ctx.state.routes[this.keepRouteKey];
    if (!keepRoute) return;

    keepRoute.markers = JSON.parse(JSON.stringify(this.keepRouteMarkersAfter));
    keepRoute.lineStrings = JSON.parse(JSON.stringify(this.keepRouteLineStringsAfter));
    keepRoute.updated_at = new Date();

    for (const { key } of this.removedRouteSnapshots) {
      delete ctx.state.routes[key];
    }

    for (const { key, position } of this.routePositionsAfter) {
      if (ctx.state.routes[key]) {
        ctx.state.routes[key].position = position;
      }
    }

    for (const key of Object.keys(ctx.state.routes)) {
      ctx.state.routes[key].day = this.targetDay;
    }

    ctx.state.activeRouteKey = this.keepRouteKey;
  }
}
